import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  beginVisit,
  emit,
  getLanguage,
  getPageLoadLatency,
  getVisit,
  getVisitElapsed,
  GtmEventType,
  GtmMetric,
  markVisitExit,
  rememberRadioSelection,
  toIsoTimestamp,
  trackButtonClick,
  trackCheckboxChange,
  trackLinkClick,
  trackPageExit,
  trackRadioChange,
} from "../services/gtm/gtmTracker";
import { buildWireId, getPageName, kebabToPascal } from "../services/gtm/pageNames";

const SETTLE_MS = 100;

function linkTargetUrl(element) {
  const href = element.getAttribute("href") || "";
  if (
    !href ||
    href === "#" ||
    href.startsWith("#") ||
    /^mailto:/i.test(href) ||
    /^tel:/i.test(href) ||
    /^javascript:/i.test(href)
  ) {
    return "";
  }
  return element.href || "";
}

function safeNewValue(value) {
  const text = String(value || "").trim();
  if (!text || text.length > 40) {
    return "";
  }
  if (/@|\d{7,}|\d{3}-\d{2}-\d{4}|\d{3}-\d{3}-\d{4}/.test(text)) {
    return "";
  }
  return text;
}

function handleDocumentClick(event, pageNameRef) {
  const element = event.target?.closest?.("[data-trackid]");
  if (!element) {
    return;
  }
  if (element.matches("input[type='radio'], input[type='checkbox'], select, textarea")) {
    return;
  }

  const trackId = element.getAttribute("data-trackid");
  if (!trackId || trackId === "none") {
    return;
  }

  const pageName = pageNameRef.current;
  if (!pageName) {
    return;
  }

  const wireId = buildWireId(pageName, trackId);
  const trackAsButton = element.getAttribute("data-track-type") === "button";
  if (element.tagName === "A" && !trackAsButton) {
    const payload = { pageName, linkId: wireId };
    const targetURL = linkTargetUrl(element);
    if (targetURL) {
      payload.targetURL = targetURL;
    }
    trackLinkClick(payload);
    return;
  }

  const payload = { pageName, buttonId: wireId };
  const newValue = safeNewValue(element.getAttribute("data-track-value"));
  if (newValue) {
    payload.newValue = newValue;
  }
  trackButtonClick(payload);
}

function handleSelectionChange(event, pageNameRef) {
  const element = event.target;
  if (!element?.matches?.("input[type='radio'], input[type='checkbox']")) {
    return;
  }

  const trackId = element.getAttribute("data-trackid");
  if (!trackId || trackId === "none") {
    return;
  }

  const pageName = pageNameRef.current;
  if (!pageName) {
    return;
  }

  if (element.type === "checkbox") {
    trackCheckboxChange({
      pageName,
      checkboxId: buildWireId(pageName, trackId),
      checked: Boolean(element.checked),
    });
    return;
  }

  const groupId = element.getAttribute("data-track-group") || "selection";
  const radioId = buildWireId(pageName, groupId);
  const selectedRadioId = `${radioId}:${kebabToPascal(trackId)}`;
  const groupKey = `${pageName}:${element.name || groupId}`;
  const previouslySelectedRadioId = rememberRadioSelection(groupKey, selectedRadioId);

  trackRadioChange({
    pageName,
    radioId,
    selectedRadioId,
    previouslySelectedRadioId,
  });
}

const GtmInstrumentation = () => {
  const location = useLocation();
  const currentPageNameRef = useRef("");
  const pageBeforeCurrentRef = useRef("");
  const isInitialLoadRef = useRef(true);
  const exitedViewRef = useRef("");

  useEffect(() => {
    const onClick = (event) => {
      try {
        handleDocumentClick(event, currentPageNameRef);
      } catch (error) {
        console.error("GTM click tracking failed:", error);
      }
    };
    const onChange = (event) => {
      try {
        handleSelectionChange(event, currentPageNameRef);
      } catch (error) {
        console.error("GTM selection tracking failed:", error);
      }
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("change", onChange, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("change", onChange, true);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const pathname = location.pathname;
    const isInitialLoad = isInitialLoadRef.current;
    const delay = isInitialLoad ? SETTLE_MS : 0;

    const timer = setTimeout(() => {
      if (cancelled) {
        return;
      }

      const pageName = getPageName(pathname);
      const pageBeingLeft = currentPageNameRef.current;
      if (pageName === pageBeingLeft) {
        return;
      }

      if (pageBeingLeft) {
        const timeOnPage = getVisitElapsed();
        if (timeOnPage > 0) {
          const visit = getVisit();
          const epochMs = (visit?.t1Epoch || Date.now()) + timeOnPage;
          markVisitExit(epochMs);
          exitedViewRef.current = visit?.pageViewId || "";
          trackPageExit({
            pageName: pageBeingLeft,
            previousPageName: pageBeforeCurrentRef.current || "",
            pageViewId: visit?.pageViewId,
            language: getLanguage(),
            timeOnPage,
            eventTimestampEpochMs: epochMs,
            eventTimestampUtc: toIsoTimestamp(epochMs),
          });
        }
      }

      const started = beginVisit({
        pageName,
        previousPageName: pageBeingLeft,
        isSoftNav: Boolean(pageBeingLeft),
      });
      pageBeforeCurrentRef.current = pageBeingLeft;
      currentPageNameRef.current = pageName;
      exitedViewRef.current = "";
      isInitialLoadRef.current = false;

      emit(GtmMetric.PageView, {
        eventType: GtmEventType.PageLoad,
        pageName,
        previousPageName: pageBeingLeft || "",
        pageViewId: started.pageViewId,
        pageLoadLatency: getPageLoadLatency(isInitialLoad),
        language: getLanguage(),
        isDomLoad: false,
        eventTimestampEpochMs: started.t1Epoch,
        eventTimestampUtc: toIsoTimestamp(started.t1Epoch),
      });
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [location.pathname]);

  useEffect(() => {
    const onPageHide = () => {
      const pageName = currentPageNameRef.current;
      const visit = getVisit();
      if (!pageName || !visit || exitedViewRef.current === visit.pageViewId) {
        return;
      }
      const timeOnPage = getVisitElapsed();
      const epochMs = visit.t1Epoch + timeOnPage;
      markVisitExit(epochMs);
      exitedViewRef.current = visit.pageViewId;
      trackPageExit(
        {
          pageName,
          previousPageName: pageBeforeCurrentRef.current || "",
          pageViewId: visit.pageViewId,
          language: getLanguage(),
          timeOnPage,
          exitReason: "browser-exit",
          eventTimestampEpochMs: epochMs,
          eventTimestampUtc: toIsoTimestamp(epochMs),
        },
        { unload: true }
      );
    };

    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  return null;
};

export default GtmInstrumentation;
